import { BottomCallout } from "@/components/BottomCallout";
import { Button } from "@/components/Button";
import { ProgressBar } from "@/components/ProgressBar";
import { TopCallout } from "@/components/TopCallout";
import { ShowToast } from "@/services/toast";
import { getUser, getToken } from "@/services/utils";
import { faShieldAlt, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect } from "react";
import { ManualUpload } from "../components/ManualUpload";
import { QrCodeUpload } from "../components/QrCodeUpload";
import { RegistrationFormData } from "../context/registration-context";


interface InsuranceFormProps {
  onContinue: () => void;
  onBack?: () => void;
  formData: RegistrationFormData;
  onGetInsurance?: () => void;
  uploadInsurance: (frontFile: File | null, backFile: File | null, isPrimary: boolean) => Promise<void>;
  saving?: boolean;
}

export const InsuranceForm: React.FC<InsuranceFormProps> = ({
  onContinue,
  onBack,
  formData,
  onGetInsurance,
  uploadInsurance,
  saving: externalSaving = false
}) => {
  const [localSaving, setLocalSaving] = useState(false);
  
  // Use external saving state if provided, otherwise use local state
  const saving = externalSaving || localSaving;
  const [frontSide, setFrontSide] = useState<File | null>(null);
  const [backSide, setBackSide] = useState<File | null>(null);
  const [secondaryFrontSide, setSecondaryFrontSide] = useState<File | null>(null);
  const [secondaryBackSide, setSecondaryBackSide] = useState<File | null>(null);
  const [isSecondaryInsuranceSelected, setIsSecondaryInsuranceSelected] = useState(false);
  const [isQrCodeShowing, setIsQrCodeShowing] = useState(true);
  
  const user = getUser();
  const token = getToken();
  
  // Get insurance data from formData
  const primaryInsurance = formData.insuranceFiles?.find((insurance: any) => insurance.isPrimary);
  const secondaryInsurance = formData.insuranceFiles?.find((insurance: any) => !insurance.isPrimary);
  
  // Poll for insurance data when QR code is showing
  useEffect(() => {
    if (isQrCodeShowing && onGetInsurance) {
      const interval = setInterval(() => {
        console.log('🔄 Polling for insurance data...');
        onGetInsurance();
      }, 5000); // Poll every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [isQrCodeShowing, onGetInsurance]);



  

  const handleUploadType = () => {
    setIsQrCodeShowing(!isQrCodeShowing);
  };

  const handleFileChange = async (file, side, type = "primary") => {
    console.log('🔄 handleFileChange called:', { file, side, type });
    
    try {
      // Set local saving state if external saving is not being used
      if (!externalSaving) {
        setLocalSaving(true);
      }
      
      // If removing existing insurance (file is null), upload null files to delete from backend
      if (file === null) {
        console.log('🗑️ Removing existing insurance:', { side, type });
        
        if (type === "primary") {
          setFrontSide(null);
          setBackSide(null);
          // Upload null files to delete primary insurance from backend
          await uploadInsurance(null, null, true);
        } else if (type === "secondary") {
          setSecondaryFrontSide(null);
          setSecondaryBackSide(null);
          // Upload null files to delete secondary insurance from backend
          await uploadInsurance(null, null, false);
        }
        return;
      }
      
      // Handle new file uploads
      if (type === "primary") {
        if (side === "front") {
          console.log('📝 Setting primary front side:', file);
          setFrontSide(file);
        }
        if (side === "back") {
          console.log('📝 Setting primary back side:', file);
          setBackSide(file);
        }
        
        // Check if we have both images for primary insurance
        const newFrontSide = side === "front" ? file : frontSide;
        const newBackSide = side === "back" ? file : backSide;
        
        if (newFrontSide && newBackSide) {
          await uploadInsurance(newFrontSide, newBackSide, true);
        }
      } else {
        if (side === "front") {
          console.log('📝 Setting secondary front side:', file);
          setSecondaryFrontSide(file);
        }
        if (side === "back") {
          console.log('📝 Setting secondary back side:', file);
          setSecondaryBackSide(file);
        }
        
        // Check if we have both images for secondary insurance
        const newFrontSide = side === "front" ? file : secondaryFrontSide;
        const newBackSide = side === "back" ? file : secondaryBackSide;
        
        if (newFrontSide && newBackSide) {
          await uploadInsurance(newFrontSide, newBackSide, false);
        }
      }
    } catch (error) {
      console.error('❌ Error in handleFileChange:', error);
    } finally {
      // Clear local saving state if we were managing it
      if (!externalSaving) {
        setLocalSaving(false);
      }
    }
  };

  return (
    <>
      <TopCallout 
        icon={faShieldAlt}
        title="We'll Handle the Insurance Legwork"
      >
       A few details here lets us verify coverage and save you time on hold. You're almost there.
      </TopCallout>

      <div className="bg-white p-8 rounded-[16px] shadow-lg">
        <ProgressBar currentStep={3} totalSteps={5} />
        

        <form className="mt-4 flex flex-col gap-[22px] text-sm lg:text-base">
          {/* Primary Insurance Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Primary Insurance</h3>

            {!primaryInsurance?.frontImg &&
            !primaryInsurance?.backImg &&
            !frontSide &&
            !backSide ? (
              // No insurance URLs exist - show QR code or manual upload
              <>
                {isQrCodeShowing ? (
                  <QrCodeUpload
                    userId={user.id}
                    token={token}
                    isPrimary={true}
                  />
                ) : (
                  <ManualUpload
                    onChange={(file, side) =>
                      handleFileChange(file, side, "primary")
                    }
                    frontSide={frontSide}
                    backSide={backSide}
                  />
                )}

                <Button
                  variant="link"
                  className="!text-primary !font-medium !text-base"
                  onClick={handleUploadType}
                  type="button"
                >
                  {isQrCodeShowing
                    ? "Upload photos manually"
                    : "Use QR code to upload photos"}
                </Button>
              </>
            ) : (
              // At least one URL exists - show manual upload
              <>
                <ManualUpload
                  onChange={(file, side) =>
                    handleFileChange(file, side, "primary")
                  }
                  frontSide={primaryInsurance?.frontImg || frontSide}
                  backSide={primaryInsurance?.backImg || backSide}
                  showValidation={false}
                />
                {!primaryInsurance?.frontImg && !primaryInsurance?.backImg && (
                  <Button
                    variant="link"
                    className="!text-primary !font-medium !text-base"
                    onClick={handleUploadType}
                    type="button"
                  >
                    Use QR code to upload photos
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Secondary Insurance Section */}
          {primaryInsurance &&
            !secondaryInsurance?.frontImg &&
            !secondaryInsurance?.backImg &&
            !isSecondaryInsuranceSelected && (
              <Button
                variant="link"
                className="!text-primary !font-medium !text-base"
                onClick={() => setIsSecondaryInsuranceSelected(true)}
                type="button"
              >
                Do you have secondary insurance?
              </Button>
            )}

          {(isSecondaryInsuranceSelected ||
            secondaryInsurance?.frontImg ||
            secondaryInsurance?.backImg) && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Secondary Insurance
              </h3>

              <ManualUpload
                onChange={(file, side) =>
                  handleFileChange(file, side, "secondary")
                }
                frontSide={secondaryInsurance?.frontImg || secondaryFrontSide}
                backSide={secondaryInsurance?.backImg || secondaryBackSide}
                showValidation={false}
              />

              {isSecondaryInsuranceSelected && (
                <Button
                  variant="link"
                  className="!text-primary !font-medium !text-base"
                  onClick={() => setIsSecondaryInsuranceSelected(false)}
                  type="button"
                >
                  Don't have secondary insurance?
                </Button>
              )}
            </div>
          )}

          <div className="mt-2">
            <div className="flex gap-3">
              {onBack && (
                <Button
                  type="button"
                  variant="gray"
                  className="!rounded-full flex-1"
                  onClick={onBack}
                  disabled={saving}
                >
                  Back
                </Button>
              )}

              <Button
                type="submit"
                variant="primary"
                className="!rounded-full flex-1"
                onClick={onContinue}
                disabled={
                  saving || 
                  !primaryInsurance || 
                  !primaryInsurance.frontImg || 
                  !primaryInsurance.backImg
                }
                loading={saving}
              >
                Continue
              </Button>
            </div>
          </div>
        </form>
      </div>

      <BottomCallout icon={faLightbulb}>
        We know insurance can be overwhelming. You don't have to figure this out alone.
      </BottomCallout>
    </>
  );
};
